import { EmptyState, PageContainer } from '../../components';
import { useAuth } from '../auth/useAuth';
import { ChangePasswordSection } from './sections/ChangePasswordSection';
import { MfaSection } from './sections/MfaSection';
import { ProfileSection } from './sections/ProfileSection';
import { SessionsSection } from './sections/SessionsSection';
import { SignInMethodsSection } from './sections/SignInMethodsSection';

/**
 * Account → Security: the per-user screen, not the workspace-admin one.
 *
 * `features/settings/SettingsPage.tsx` is a different screen with a different
 * audience — workspace terms and people, gated on `workspace.admin` —
 * and this page shares none of its code on purpose (see SectionCard.tsx). This
 * one belongs to whoever is signed in, about their own nauth identity: no
 * capability check, because there is no workspace-scoped permission that
 * applies to "can I see my own password form."
 */
export function AccountSecurityPage() {
  const { user, isLoading } = useAuth();

  if (isLoading) return <p className="px-5 pt-9 fs-12 text-muted md:px-10">Loading…</p>;
  if (user === null) {
    return (
      <div className="mx-auto w-full max-w-[45rem] px-5 pt-9 md:px-10">
        <EmptyState title="Not signed in" description="Sign in to see your account." />
      </div>
    );
  }

  return (
    <PageContainer>
      <h1 className="fs-26 leading-tight font-bold tracking-[-0.02em] text-ink">Account</h1>
      <p className="mt-1.5 mb-6 max-w-[35rem] fs-13 text-muted">
        Your sign-in details and security settings.
      </p>

      <div className="flex flex-col gap-4">
        <ProfileSection user={user} />
        <SignInMethodsSection email={user.email} />
        <ChangePasswordSection />
        <SessionsSection />
        <MfaSection />
      </div>
    </PageContainer>
  );
}
